import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { DeveloperProvider } from "@/contexts/DeveloperContext";
import { WindowManagerProvider } from "@/contexts/WindowManagerContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingScreen } from "@/components/LoadingScreen";
import { FloatingButtons } from "./components/FloatingButtons";
import { SessionManager } from "./components/SessionManager";

import { WindowTaskbar } from "./components/window-manager/WindowTaskbar";
import { WindowRenderer } from "./components/window-manager/WindowRenderer";

// Lazy load all route components for better code splitting
const Home = lazy(() => import("./pages/Home"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Download = lazy(() => import("./pages/Download"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Index = lazy(() => import("./pages/Index"));
const InventoryDashboard = lazy(() => import("@/pages/inventory/InventoryDashboard"));
const ProductList = lazy(() => import("@/pages/inventory/ProductList"));
const ProductCatalog = lazy(() => import("@/pages/inventory/ProductCatalog"));
const StockManagement = lazy(() => import("@/pages/inventory/StockManagement"));
const CustomerList = lazy(() => import("@/pages/inventory/CustomerList"));
const VendorList = lazy(() => import("@/pages/inventory/VendorList"));
const CustomerLedger = lazy(() => import("@/pages/inventory/CustomerLedger"));
const VendorLedger = lazy(() => import("@/pages/inventory/VendorLedger"));
const LedgerAnalytics = lazy(() => import("@/pages/inventory/LedgerAnalytics"));
const LedgerAudit = lazy(() => import("@/pages/LedgerAudit"));
const Reports = lazy(() => import("@/pages/inventory/Reports"));
const Reminders = lazy(() => import("@/pages/inventory/Reminders"));
const History = lazy(() => import("@/pages/inventory/History"));
const HistoryProducts = lazy(() => import("@/pages/inventory/HistoryProducts"));
const HistoryCustomers = lazy(() => import("@/pages/inventory/HistoryCustomers"));
const HistoryVendors = lazy(() => import("@/pages/inventory/HistoryVendors"));
const Auth = lazy(() => import("./pages/Auth").then(module => ({ default: module.Auth })));
const OTPVerify = lazy(() => import("./pages/OTPVerify").then(module => ({ default: module.OTPVerify })));
const ModuleSelection = lazy(() => import("./pages/ModuleSelection").then(module => ({ default: module.ModuleSelection })));
const Settings = lazy(() => import("./pages/Settings").then(module => ({ default: module.Settings })));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ResetPasswordVerify = lazy(() => import("./pages/ResetPasswordVerify"));
const ConfirmPasswordReset = lazy(() => import("./pages/ConfirmPasswordReset"));
const ChangeEmail = lazy(() => import("./pages/ChangeEmail"));
const ChangeEmailVerify = lazy(() => import("./pages/ChangeEmailVerify"));
const DeveloperDashboard = lazy(() => import("./pages/DeveloperDashboard"));
const AuthDiagnostics = lazy(() => import("./pages/AuthDiagnostics"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Conditional taskbar component that only renders on IMS pages
const ConditionalTaskbar = () => {
  const location = useLocation();
  const isIMSPage = location.pathname.startsWith('/inventory') || location.pathname.startsWith('/app/inventory');
  
  if (!isIMSPage) return null;
  return <WindowTaskbar />;
};

const queryClient = new QueryClient();

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Show loading screen for 2.5 seconds on initial load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
      <LanguageProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <PreferencesProvider>
          <CurrencyProvider>
            <DeveloperProvider>
              <WindowManagerProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Suspense fallback={<LoadingScreen />}>
                  <main className="min-h-screen">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:id" element={<BlogPost />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/download" element={<Download />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/app" element={<Index />} />
                    <Route path="/app/cashbook" element={<Index />} />
                    <Route path="/app/inventory" element={<InventoryDashboard />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/verify-otp" element={<OTPVerify />} />
                    <Route path="/module-selection" element={<ModuleSelection />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/reset-password-verify" element={<ResetPasswordVerify />} />
                    <Route path="/confirm-reset" element={<ConfirmPasswordReset />} />
                    <Route path="/change-email" element={<ChangeEmail />} />
                    <Route path="/change-email-verify" element={<ChangeEmailVerify />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/inventory" element={<InventoryDashboard />} />
                    <Route path="/inventory/products" element={<ProductList />} />
                    <Route path="/inventory/stock" element={<StockManagement />} />
                    <Route path="/inventory/catalog" element={<ProductCatalog />} />
                    <Route path="/inventory/customers/:customerId/ledger" element={<CustomerLedger />} />
                    <Route path="/inventory/vendors/:vendorId/ledger" element={<VendorLedger />} />
                    <Route path="/inventory/ledger-analytics" element={<LedgerAnalytics />} />
                    <Route path="/inventory/reports" element={<Reports />} />
                    <Route path="/inventory/reminders" element={<Reminders />} />
                    <Route path="/inventory/history" element={<History />} />
                    <Route path="/inventory/history/products" element={<HistoryProducts />} />
                    <Route path="/inventory/history/customers" element={<HistoryCustomers />} />
                    <Route path="/inventory/history/vendors" element={<HistoryVendors />} />
                    <Route path="/inventory/customers" element={<CustomerList />} />
                    <Route path="/inventory/vendors" element={<VendorList />} />
                    <Route path="/admin/ledger-audit" element={<LedgerAudit />} />
                    <Route path="/admin/auth-diagnostics" element={<AuthDiagnostics />} />
                    <Route path="/developer" element={<DeveloperDashboard />} />
                    <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </main>
                  </Suspense>
                  <SessionManager />
                  <FloatingButtons />
                  <WindowRenderer />
                  <ConditionalTaskbar />
                </BrowserRouter>
              </TooltipProvider>
              </WindowManagerProvider>
            </DeveloperProvider>
          </CurrencyProvider>
          </PreferencesProvider>
        </ThemeProvider>
      </LanguageProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
